import { SettingOutlined } from '@ant-design/icons';
import React, {
  type CSSProperties,
  type ForwardedRef,
  forwardRef,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';

import IndentIcon from '@/assets/icons/IndentIcon';
import { Flex, Space, VFlex } from '@/components/base';
import Input from '@/components/Inputs/Input';
import SelectPortal from '@/components/Select/SelectPortal';
import type { ModalOptions, TriggerPos } from '@/components/Select/types';
import { MainText } from '@/components/Typography/NamedTypo';
import useColor from '@/hooks/domain/useColor';
import useDropdownPos from '@/hooks/pure/useDropdownPos';
import useKeymap from '@/hooks/pure/useKeymap';
import useModalStore from '@/stores/pure/ModalStore';
import useTagStore from '@/stores/TagStore';
import { customScrollbar } from '@/styles/style';
import feedbackUtils from '@/utils/feedback';

import Label from './Label';
import SearchResultTag from './SearchResultTag';

interface Props {
  selectedTags?: string[];
  toggleItem: (tag: string) => void;
  closeModal: () => void;
  isShowSelectedTags?: boolean;
  modalWidth: CSSProperties['width'];
  status: boolean;
  modalOptions?: ModalOptions;
  triggerEl?: HTMLDivElement | null;
  triggerPos?: TriggerPos;
}

const TagSelectModal = (props: Props) => {
  const { dropdownPos, setDropdownRef: setModalRef } = useDropdownPos(
    props.status,
    {
      el: props.triggerEl,
      pos: props.triggerPos,
      options: props.modalOptions,
    },
  );

  return (
    <SelectPortal
      status={props.status}
      close={props.closeModal}
      style={{
        height: 500,
        ...dropdownPos,
      }}
    >
      <Content
        {...props}
        height={dropdownPos.height || 500}
        ref={setModalRef}
      />
    </SelectPortal>
  );
};

const Content = forwardRef(
  (
    {
      selectedTags,
      toggleItem,
      closeModal,
      isShowSelectedTags = true,
      modalWidth,
      height,
    }: Props & {
      height: StringNumberValue;
    },
    ref: ForwardedRef<HTMLDivElement | null>,
  ) => {
    const color = useColor();
    const tagMap = useTagStore((state) => state.tagMap);
    const setModalStatus = useModalStore((state) => state.setModalStatus);
    const lv1TagNodes = useMemo(() => {
      return Object.values(tagMap).filter((tag) => tag.level === 1);
    }, [tagMap]);

    const [searchValue, setSearchValue] = useState('');

    const emptySearchValue = useMemo(() => {
      return searchValue.trim() === '';
    }, [searchValue]);

    const matchedList: MatchedTagT[] = useMemo(() => {
      if (emptySearchValue) return [];

      return lv1TagNodes.reduce((acc, lv1Tag) => {
        return feedbackUtils.findMatchTagRecursion(
          tagMap,
          acc,
          lv1Tag.alias,
          false,
          searchValue,
        );
      }, [] as MatchedTagT[]);
    }, [emptySearchValue, tagMap, lv1TagNodes, searchValue]);

    const indexArr = useMemo(() => {
      return lv1TagNodes.reduce((acc, lv1Node) => {
        acc.push(lv1Node.alias);
        lv1Node.children?.forEach((lv2) => {
          acc.push(lv2);
          tagMap[lv2].children?.forEach((lv3) => {
            acc.push(lv3);
          });
        });
        return acc;
      }, [] as string[]);
    }, [tagMap, lv1TagNodes]);

    const [focused, setFocused] = useState<string | null>(
      lv1TagNodes[0]?.alias || null,
    );

    useEffect(() => {
      if (matchedList.length) {
        setFocused(matchedList[0].tag.alias);
      }
    }, [matchedList]);

    const focusNext = () => {
      if (!focused) {
        setFocused(indexArr[0]);
        return;
      }
      if (matchedList.length) {
        const curr = matchedList.findIndex((l) => l.tag.alias === focused);
        if (curr === matchedList.length - 1) return;
        setFocused(matchedList[curr + 1].tag.alias);
      } else {
        const curr = indexArr.findIndex((key) => key === focused);
        if (curr === indexArr.length - 1) return;
        setFocused(indexArr[curr + 1]);
      }
    };

    const focusPrev = () => {
      if (!focused) return;

      if (matchedList.length) {
        const curr = matchedList.findIndex((m) => m.tag.alias === focused);
        if (curr === 0) return;
        setFocused(matchedList[curr - 1].tag.alias);
      } else {
        const curr = indexArr.findIndex((key) => key === focused);
        if (curr === 0) return;
        setFocused(indexArr[curr - 1]);
      }
    };

    useKeymap('ArrowUp', focusPrev);
    useKeymap('ArrowDown', focusNext);
    useKeymap('Enter', () => {
      if (!focused) return;
      toggleItemHandler(focused);
    });
    useKeymap('Escape', () => {
      closeModal && closeModal();
    });

    const toggleItemHandler = (tag: string) => {
      setFocused(tag);
      setTimeout(() => {
        toggleItem(tag);
      }, 1);
    };

    return (
      <Wrapper
        f={1}
        style={{ width: modalWidth, height: `${height}px` }}
        ref={ref}
      >
        {selectedTags !== undefined &&
          selectedTags.length > 0 &&
          isShowSelectedTags && (
            <SelectedTagsWrapper>
              <Flex
                d="row"
                g="8px"
                style={{ flexWrap: 'wrap', maxWidth: '400px' }}
              >
                {selectedTags
                  .filter((key) => tagMap[key])
                  .map((key: string) => {
                    const tag = tagMap[key];
                    const tagHTML = feedbackUtils.getTagHTML(tagMap, key);

                    return (
                      <Label
                        key={tag.alias}
                        themeColor={tag.color}
                        text={
                          <span
                            dangerouslySetInnerHTML={{
                              __html: tagHTML,
                            }}
                          ></span>
                        }
                        isSelected={false}
                        cancellable
                        onDeleteClick={() => toggleItem(tag.alias)}
                        style={{ borderColor: color.gray600 }}
                      />
                    );
                  })}
              </Flex>
            </SelectedTagsWrapper>
          )}
        <Input
          sizeType="m"
          initFocus
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.code === 'ArrowUp') {
              focusPrev();
              e.preventDefault();
            }
            if (e.code === 'ArrowDown') {
              focusNext();
              e.preventDefault();
            }
            if (e.code === 'Enter') {
              if (!focused) return;
              toggleItem(focused);
              e.preventDefault();
            }
            if (e.code === 'Escape') {
              closeModal && closeModal();
              e.preventDefault();
            }
          }}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Search or select from the list"
        />
        <VFlex f={1} style={{ overflow: 'auto' }}>
          {emptySearchValue ? (
            <VFlex className="tag-wrapper">
              {Object.keys(tagMap).length > 0 ? (
                lv1TagNodes.map((lv1TagNode, i) => {
                  // List up level-1 tags
                  return (
                    <VFlex key={lv1TagNode.alias} a="flex-start" g="8px">
                      <Label
                        text={lv1TagNode.name}
                        themeColor={lv1TagNode.color}
                        focused={focused === lv1TagNode.alias}
                        isSelected={
                          !!selectedTags?.find(
                            (key) => key === lv1TagNode.alias,
                          )
                        }
                        onClick={() => toggleItemHandler(lv1TagNode.alias)}
                      />
                      {lv1TagNode.children.map((lv2TagNodeAlias, j) => {
                        // List up level-2 tags
                        return (
                          <React.Fragment key={lv2TagNodeAlias}>
                            <Flex g="8px">
                              <IndentIcon color={color.gray400} />
                              <Label
                                text={tagMap[lv2TagNodeAlias].name}
                                themeColor={tagMap[lv2TagNodeAlias].color}
                                focused={focused === lv2TagNodeAlias}
                                isSelected={
                                  !!selectedTags?.find(
                                    (key) => key === lv2TagNodeAlias,
                                  )
                                }
                                onClick={() =>
                                  toggleItemHandler(lv2TagNodeAlias)
                                }
                              />
                            </Flex>
                            {tagMap[lv2TagNodeAlias]?.children.map(
                              (lv3TagNodeAlias, k) => {
                                // List up level-3 tags
                                return (
                                  <Flex key={lv3TagNodeAlias} g="8px">
                                    <Space w="22px" />
                                    <IndentIcon color={color.gray400} />
                                    <Label
                                      text={tagMap[lv3TagNodeAlias].name}
                                      themeColor={tagMap[lv3TagNodeAlias].color}
                                      focused={focused === lv3TagNodeAlias}
                                      isSelected={
                                        !!selectedTags?.find(
                                          (key) => key === lv3TagNodeAlias,
                                        )
                                      }
                                      onClick={() =>
                                        toggleItemHandler(lv3TagNodeAlias)
                                      }
                                    />
                                  </Flex>
                                );
                              },
                            )}
                          </React.Fragment>
                        );
                      })}
                      <Space h="0px" />
                    </VFlex>
                  );
                })
              ) : (
                <VFlex className="tag-wrapper">
                  <MainText dim>No tag</MainText>
                </VFlex>
              )}
            </VFlex>
          ) : matchedList.length > 0 ? (
            <VFlex g={8} className="tag-wrapper">
              {matchedList.map((searchResult) => (
                <SearchResultTag
                  key={searchResult.tag.alias}
                  {...searchResult}
                  searchString={searchValue}
                  toggleItem={toggleItemHandler}
                  isSelected={
                    !!selectedTags?.find(
                      (key) => key === searchResult.tag.alias,
                    )
                  }
                  focused={focused == searchResult.tag.alias}
                />
              ))}
            </VFlex>
          ) : (
            <VFlex className="tag-wrapper">
              <MainText dim>No tag</MainText>
            </VFlex>
          )}
        </VFlex>
        <SettingButton
          g="8px"
          a="center"
          j="flex-end"
          onClick={() => {
            closeModal?.();
            setModalStatus('tagConfigModal', true);
          }}
        >
          <SettingOutlined style={{ color: color.gray600, fontSize: 12 }} />
          <MainText dim>Go to Settings&nbsp; {'>'} &nbsp;Tags</MainText>
        </SettingButton>
      </Wrapper>
    );
  },
);
const Wrapper = styled(VFlex)`
  padding: 8px;
  gap: 12px;
  overflow: auto;
`;

const SelectedTagsWrapper = styled(VFlex)`
  overflow-y: auto;
  max-height: 80px;
  ${customScrollbar};
`;

const SettingButton = styled(Flex)`
  width: 100%;
  padding: 8px 12px;
  cursor: pointer;
  margin: 0 auto 0 0;

  &:hover {
    background: ${(props) => props.theme.colors.gray200};
  }
`;

export default TagSelectModal;
